import { FC, useMemo, useState } from 'react';

import { Search2Line } from '@assets/icons';
import Collapse from '@components/Collapse/Collapse';
import MultiSelectionFilter from '@components/ui/filters/MultiSelectionFilter/MultiSelectionFilter';
import {
  ListingFilterItemInterface,
  ListingFilterItemKey,
} from '@components/ui/filters/MultiSelectionFilter/types';
import CustomIcon from '@components/utils/CustomIcon';

import {
  TourFilterItemsWrapper,
  TourFilterSectionContent,
  TourFilterSectionTitle,
  TourFilterSectionWrapper,
  TourFilterSearchInput,
  TourFilterSearchWrapper,
} from './TourFilterSectionStyle';

interface TourFilterSectionProps {
  title: string;
  data: ListingFilterItemInterface[];
  selectedItems: ListingFilterItemKey[];
  handleSelectedItems: (items: ListingFilterItemKey[]) => void;
  searchable?: boolean;
  collapsible?: boolean;
  showMore?: boolean;
  subTitleKey?: string;
}

const VISIBLE_ITEMS_LIMIT = 5;
const SCROLLABLE_ITEMS_THRESHOLD = 10;

const TourFilterSection: FC<TourFilterSectionProps> = ({
  title,
  data,
  selectedItems,
  handleSelectedItems,
  searchable = false,
  collapsible = false,
  showMore = true,
  subTitleKey = 'count',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  const filteredItems = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLocaleLowerCase('tr-TR');
    if (!normalizedSearch) return data;

    return data.filter((item) =>
      item.value.toLocaleLowerCase('tr-TR').includes(normalizedSearch)
    );
  }, [data, searchTerm]);

  if (!data.length) return null;

  const isTruncatable = showMore;
  const items =
    isTruncatable && !isExpanded
      ? filteredItems.slice(0, VISIBLE_ITEMS_LIMIT)
      : filteredItems;
  const canShowMore =
    isTruncatable && filteredItems.length > VISIBLE_ITEMS_LIMIT;
  const content = (
    <TourFilterSectionContent>
      {searchable ? (
        <TourFilterSearchWrapper>
          <CustomIcon icon={Search2Line} paletteColor='grey500' fontSize={20} />
          <TourFilterSearchInput
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder='Ara'
            aria-label={`${title} ara`}
          />
        </TourFilterSearchWrapper>
      ) : null}
      <TourFilterItemsWrapper
        $isScrollable={
          isExpanded && filteredItems.length > SCROLLABLE_ITEMS_THRESHOLD
        }
      >
        <MultiSelectionFilter
          data={items}
          selectedItems={selectedItems}
          handleSelectedItems={handleSelectedItems}
          showSubTitle
          subTitleKey={subTitleKey}
        />
      </TourFilterItemsWrapper>
      {canShowMore ? (
        <button type='button' onClick={() => setIsExpanded((value) => !value)}>
          {isExpanded ? 'Daha Az Göster' : 'Daha Fazla Göster'}
        </button>
      ) : null}
    </TourFilterSectionContent>
  );

  return (
    <TourFilterSectionWrapper>
      {collapsible ? (
        <Collapse
          defaultActiveKey={selectedItems.length ? 'tour-filter' : undefined}
          panelProps={{
            header: <TourFilterSectionTitle>{title}</TourFilterSectionTitle>,
            key: 'tour-filter',
          }}
        >
          {content}
        </Collapse>
      ) : (
        <>
          <TourFilterSectionTitle>{title}</TourFilterSectionTitle>
          {content}
        </>
      )}
    </TourFilterSectionWrapper>
  );
};

export default TourFilterSection;
